/* eslint-disable no-param-reassign */
import { useMsal } from '@azure/msal-react';
import axios, { AxiosError, AxiosResponse, type AxiosInstance } from 'axios';
import axiosRetry from 'axios-retry';
import { useIntl } from 'react-intl';
import { loginRequest } from '../../authConfig';
import { haltForInteractiveReauth, isInteractionRequiredError } from '../../features/Auth/interactiveReauth';
import localizedMessages from './messages';

import { type AccountInfo, type AuthenticationResult, type IPublicClientApplication } from '@azure/msal-browser';
import {
  makeAssetsApiHeaders,
  makeCoresApiHeaders,
  makeCustomerSettingsApiHeaders,
  makeShipmentsApiHeaders,
  makeUsersApiHeaders,
} from '../headers';
import logger from '../middleware/log';
import {
  ACTIVE_PERSONA_USER_ID_KEY,
  IMPERSONATION_SESSION_ID_KEY,
  LOGIKSPARK_CORE_API_URL,
  LOGIKSPARK_PRODUCTS_API_URL,
  LOGIKSPARK_ASSETS_API_URL,
  LOGIKSPARK_ORDERS_API_URL,
  LOGIKSPARK_USERS_API_URL
} from './const';

export const shipmentsUnathorizedLogikSparkFetch = axios.create({
  baseURL: LOGIKSPARK_ORDERS_API_URL,
  headers: makeShipmentsApiHeaders(),
})

export const usersUnathorizedLogikSparkFetch = axios.create({
  baseURL: LOGIKSPARK_ORDERS_API_URL,
  headers: makeUsersApiHeaders(),
})

export const coresUnathorizedLogikSparkFetch = axios.create({
  baseURL: LOGIKSPARK_CORE_API_URL,
  headers: makeCoresApiHeaders(),
})

export const productUanthorizedLogikSparkFetch = axios.create({
  baseURL: LOGIKSPARK_PRODUCTS_API_URL,
  headers: makeShipmentsApiHeaders(),
})

export let shipmentsAuthorizedFetch: AxiosInstance = shipmentsUnathorizedLogikSparkFetch
export let usersAuthorizedFetch: AxiosInstance = usersUnathorizedLogikSparkFetch
export let coresAuthorizedFetch: AxiosInstance = coresUnathorizedLogikSparkFetch
export let productAuthorizedFetch: AxiosInstance = productUanthorizedLogikSparkFetch
export let assetsAuthorizedFetch: AxiosInstance = usersUnathorizedLogikSparkFetch
export let customerSettingsAuthorizedFetch: AxiosInstance = usersUnathorizedLogikSparkFetch

export const googleApiFetch: AxiosInstance = axios.create({
  baseURL: 'https://maps.googleapis.com/maps/api',
})

// Only HTTP-idempotent methods are re-sent, and only on a 5xx or an aborted request. A POST that failed
// with a 5xx may already have committed (or compensated) server-side, so a blind replay hits a different
// error than the real one and hides it: create-federation-provider answered 503, the replay got 400
// "This consent already backs a federation provider", and the user never learned what failed (D20).
const IDEMPOTENT_METHODS = ['get', 'head', 'options', 'put', 'delete']

export const isRetryableTransientError = (error: AxiosError): boolean =>
  IDEMPOTENT_METHODS.includes(error.config?.method?.toLowerCase() ?? '') &&
  ((error.response?.status ?? 0) >= 500 || error.code === 'ECONNABORTED')

/**
 * Sets up axios instances with correct base URLs for Docker dev mode (no auth tokens).
 * Called from DevApp instead of AuthorizedAxios which requires MSAL.
 */
export const setupDevAxios = (): void => {
  shipmentsAuthorizedFetch = axios.create({
    baseURL: LOGIKSPARK_ORDERS_API_URL,
    headers: makeShipmentsApiHeaders(),
  })

  usersAuthorizedFetch = axios.create({
    baseURL: LOGIKSPARK_USERS_API_URL,
    headers: makeUsersApiHeaders(),
  })

  axiosRetry(usersAuthorizedFetch, {
    retries: 3,
    retryCondition: (error: any) => {
      logger.error(error)
      return isRetryableTransientError(error)
    },
  })

  assetsAuthorizedFetch = axios.create({
    baseURL: LOGIKSPARK_ASSETS_API_URL,
    headers: makeAssetsApiHeaders(),
  })

  coresAuthorizedFetch = axios.create({
    baseURL: LOGIKSPARK_CORE_API_URL,
    headers: makeCoresApiHeaders(),
  })

  customerSettingsAuthorizedFetch = axios.create({
    baseURL: LOGIKSPARK_ORDERS_API_URL,
    headers: makeCustomerSettingsApiHeaders(),
  })

  productAuthorizedFetch = axios.create({
    baseURL: LOGIKSPARK_PRODUCTS_API_URL,
    headers: makeShipmentsApiHeaders(),
  })

  axiosRetry(productAuthorizedFetch, {
    retries: 3,
    retryCondition: (error: any) => {
      logger.error(error)
      return isRetryableTransientError(error)
    },
  })

  axiosRetry(assetsAuthorizedFetch, {
    retries: 3,
    retryCondition: (error: any) => {
      logger.error(error)
      return isRetryableTransientError(error)
    },
  })
}

// Don't retry authentication/authorization failures — a 401/403 won't change on a blind retry, and
// retrying just delays the real error reaching the response interceptor. Shared by every authorized client below.
const retryTransientOnly = {
  retries: 3,
  retryCondition: isRetryableTransientError,
}

// Pull an MSAL claims challenge out of a 401's WWW-Authenticate header, when the API exposes it
// (needs Access-Control-Expose-Headers: WWW-Authenticate on the API). The value is base64-encoded
// claims JSON; we decode it for acquireTokenRedirect. Returns undefined when there's no challenge to
// answer, so the caller falls through to surfacing the 401.
const extractClaimsChallenge = (response: AxiosResponse): string | undefined => {
  const header = response.headers?.['www-authenticate']
  if (typeof header !== 'string') {
    return undefined
  }
  const match = /claims="?([^",]+)"?/.exec(header)
  if (!match) {
    return undefined
  }
  try {
    return atob(match[1])
  } catch {
    return match[1]
  }
}

// Single source of truth for attaching a fresh bearer token to an authorized client — replaces the
// six near-identical request interceptors that were copy-pasted across the API clients. A silent
// refresh that needs UI (expired session, new consent, or the hidden-iframe fallback dead-ending on
// third-party cookie blocking) halts the request and redirects to re-auth; any other failure is logged
// and the request proceeds tokenless so the response interceptor sees the real 401/403 rather than a
// thrown client error.
export const attachBearerRequestInterceptor = (
  client: AxiosInstance,
  instance: IPublicClientApplication,
  account: AccountInfo | null,
  noActiveAccountError: string,
): void => {
  client.interceptors.request.use(async (config) => {
    if (account == null) {
      throw Error(noActiveAccountError)
    }

    let response: AuthenticationResult | undefined
    try {
      response = await instance.acquireTokenSilent({ ...loginRequest, account })
    } catch (error) {
      if (isInteractionRequiredError(error)) {
        // Only an interactive round-trip can produce a token now — halt this request rather than
        // letting it race the redirect as a guaranteed 401 (the pre-redirect error flash).
        return haltForInteractiveReauth(instance, account)
      }
      logger.error(error)
    }

    // Only attach the header when we actually have a token — otherwise the request proceeds genuinely
    // tokenless rather than sending "Bearer undefined", which an optional-auth endpoint could mistake
    // for a real (malformed) credential.
    if (response?.accessToken) {
      config.headers.Authorization = `Bearer ${response.accessToken}`
    }

    // X-LogikSpark-Persona-Id (E7 F7-5.3): the active persona's User Id from sessionStorage (set by
    // the persona splash, E8 F8-4.1; absent during splash → header skipped). E9 F9-2's middleware
    // reads it and authoritatively re-validates against the JWT oid, so a spoofed value is rejected
    // server-side. The positive-integer guard rejects null/NaN/negatives and 0 (the reserved platform Id).
    const personaIdRaw = sessionStorage.getItem(ACTIVE_PERSONA_USER_ID_KEY)
    if (personaIdRaw) {
      const personaId = Number.parseInt(personaIdRaw, 10)
      if (Number.isInteger(personaId) && personaId > 0) {
        config.headers['X-LogikSpark-Persona-Id'] = String(personaId)
      } else {
        logger.warn('Invalid lsk.activePersona.userId in sessionStorage; skipping persona header', personaIdRaw)
      }
    }

    // X-LogikSpark-Impersonation-Session-Id (E11 R3, WI 605): the active impersonation session Id from
    // sessionStorage (set by useImpersonationStart on a successful Start; absent unless a session is live).
    // The E9 Stage-4 middleware validates it against the persona's active session and rejects a stale/foreign
    // value (403), so this is a routing convenience, not a trust boundary. Same positive-integer guard as the
    // persona header. It is cleared when the session ends (banner Stop / auto-end / sign-out), so a request
    // after the session closes carries no stale header.
    const sessionIdRaw = sessionStorage.getItem(IMPERSONATION_SESSION_ID_KEY)
    if (sessionIdRaw) {
      const sessionId = Number.parseInt(sessionIdRaw, 10)
      if (Number.isInteger(sessionId) && sessionId > 0) {
        config.headers['X-LogikSpark-Impersonation-Session-Id'] = String(sessionId)
      } else {
        logger.warn('Invalid lsk.impersonation.sessionId in sessionStorage; skipping impersonation header', sessionIdRaw)
      }
    }

    return config
  })
}

// Single response interceptor for the authorized clients. A 401 here means the API rejected an
// otherwise-valid token — an authorization outcome, not a sign-in failure — so we no longer force a
// logoutRedirect (the old behaviour signed the user out on any transient 401). The one 401 that does
// need MSAL is a claims challenge (conditional-access / step-up): re-acquire with the challenge
// (halting the failed request while the redirect navigates) rather than signing out. Everything else
// (plain 401, 403) surfaces to the caller untouched.
export const attachAuthResponseInterceptor = (
  client: AxiosInstance,
  instance: IPublicClientApplication,
  account: AccountInfo | null,
): void => {
  client.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
      if (error.response?.status === 401 && account != null) {
        const claimsChallenge = extractClaimsChallenge(error.response)
        if (claimsChallenge) {
          return haltForInteractiveReauth(instance, account, { claims: claimsChallenge })
        }
      }
      return Promise.reject(error)
    },
  )
}

export const AuthorizedAxios = async (): Promise<void> => {
  const { instance } = useMsal()
  const account = instance.getActiveAccount()
  const { formatMessage } = useIntl()
  const noActiveAccountError = formatMessage(localizedMessages.noActiveAccount)

  shipmentsAuthorizedFetch = axios.create({
    baseURL: LOGIKSPARK_ORDERS_API_URL,
    headers: makeShipmentsApiHeaders(),
  })
  usersAuthorizedFetch = axios.create({
    baseURL: LOGIKSPARK_USERS_API_URL,
    headers: makeUsersApiHeaders(),
  })
  assetsAuthorizedFetch = axios.create({
    baseURL: LOGIKSPARK_ASSETS_API_URL,
    headers: makeAssetsApiHeaders(),
  })
  coresAuthorizedFetch = axios.create({
    baseURL: LOGIKSPARK_CORE_API_URL,
    headers: makeCoresApiHeaders(),
  })
  customerSettingsAuthorizedFetch = axios.create({
    baseURL: LOGIKSPARK_ORDERS_API_URL,
    headers: makeCustomerSettingsApiHeaders(),
  })
  productAuthorizedFetch = axios.create({
    baseURL: LOGIKSPARK_PRODUCTS_API_URL,
    headers: makeShipmentsApiHeaders(),
  })

  // Same token-attach + 401/403 handling on every authorized client (was duplicated six times).
  const authorizedClients: AxiosInstance[] = [
    shipmentsAuthorizedFetch,
    usersAuthorizedFetch,
    assetsAuthorizedFetch,
    coresAuthorizedFetch,
    customerSettingsAuthorizedFetch,
    productAuthorizedFetch,
  ]
  authorizedClients.forEach((client) => {
    attachBearerRequestInterceptor(client, instance, account, noActiveAccountError)
    attachAuthResponseInterceptor(client, instance, account)
  })

  axiosRetry(usersAuthorizedFetch, retryTransientOnly)
  axiosRetry(productAuthorizedFetch, retryTransientOnly)
  axiosRetry(assetsAuthorizedFetch, retryTransientOnly)
}
